import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { LanguageProvider, detectLanguageFromPath } from "@/contexts/LanguageContext";
import { ADMIN_PATH } from "@/config/site.config";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import ScriptsInjector from "./components/ScriptsInjector";

// Lazy load pages that are not always needed
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));

// Simple loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const queryClient = new QueryClient();

const AppRoutes = () => {
  const location = useLocation();
  const language = detectLanguageFromPath(location.pathname);

  return (
    <LanguageProvider initialLanguage={language}>
      <ScriptsInjector />
      <Routes>
        {/* English (default) */}
        <Route path="/" element={<Index />} />
        <Route path="/privacy" element={<Suspense fallback={<PageLoader />}><PrivacyPage /></Suspense>} />
        <Route path="/contact" element={<Suspense fallback={<PageLoader />}><ContactPage /></Suspense>} />
        
        {/* Ukrainian */}
        <Route path="/ua" element={<Index />} />
        <Route path="/ua/privacy" element={<Suspense fallback={<PageLoader />}><PrivacyPage /></Suspense>} />
        <Route path="/ua/contact" element={<Suspense fallback={<PageLoader />}><ContactPage /></Suspense>} />
        
        {/* Russian */}
        <Route path="/ru" element={<Index />} />
        <Route path="/ru/privacy" element={<Suspense fallback={<PageLoader />}><PrivacyPage /></Suspense>} />
        <Route path="/ru/contact" element={<Suspense fallback={<PageLoader />}><ContactPage /></Suspense>} />
        
        {/* Spanish */}
        <Route path="/es" element={<Index />} />
        <Route path="/es/privacy" element={<Suspense fallback={<PageLoader />}><PrivacyPage /></Suspense>} />
        <Route path="/es/contact" element={<Suspense fallback={<PageLoader />}><ContactPage /></Suspense>} />
        
        {/* Portuguese */}
        <Route path="/pt" element={<Index />} />
        <Route path="/pt/privacy" element={<Suspense fallback={<PageLoader />}><PrivacyPage /></Suspense>} />
        <Route path="/pt/contact" element={<Suspense fallback={<PageLoader />}><ContactPage /></Suspense>} />
        
        {/* French */}
        <Route path="/fr" element={<Index />} />
        <Route path="/fr/privacy" element={<Suspense fallback={<PageLoader />}><PrivacyPage /></Suspense>} />
        <Route path="/fr/contact" element={<Suspense fallback={<PageLoader />}><ContactPage /></Suspense>} />
        
        {/* German */}
        <Route path="/de" element={<Index />} />
        <Route path="/de/privacy" element={<Suspense fallback={<PageLoader />}><PrivacyPage /></Suspense>} />
        <Route path="/de/contact" element={<Suspense fallback={<PageLoader />}><ContactPage /></Suspense>} />
        
        {/* Admin - фиксированный путь, защита через .htpasswd на хостинге */}
        <Route path={ADMIN_PATH} element={<Suspense fallback={<PageLoader />}><AdminPage /></Suspense>} />
        
        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </LanguageProvider>
  );
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
